import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "./useMediaQuery";

const STORAGE_KEY = "celszc.tvMode";

/** Common Smart TV / streaming-box / web TV browser signatures */
const TV_UA =
  /SmartTV|SMART-TV|Smart-TV|WebTV|AppleTV|GoogleTV|Android.?TV|HbbTV|Tizen|Web0S|webOS|WEBOS|BRAVIA|CrKey|AFT[A-Z0-9]|Viera|NetCast|TV Store|SonyDTV|MIBOX|MiBOX|FireTV|Fire.?TV|Silk\/|DTVNetBrowser|Maple|SmartHub|Large Screen|TV Safari|Hisense|Vizio|Roku|PlayStation|Xbox|Nintendo|NVIDIA Shield|SHIELD Android TV|Chromecast|Xfinity|Comcast|SkyQ|PhilipsTV|Panasonic|Toshiba|Sharp.?TV|Opera TV|TV Browser|Smart.?TV.?Browser/i;

function readStoredPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    // ignore
  }
  return null;
}

function detectTvUserAgent() {
  if (typeof navigator === "undefined") return false;
  const ua = `${navigator.userAgent || ""} ${navigator.vendor || ""} ${navigator.platform || ""}`;
  if (TV_UA.test(ua)) return true;

  // Chromium Client Hints (when available)
  try {
    const brands = navigator.userAgentData?.brands || [];
    if (brands.some((b) => /tv|tizen|webos|smart/i.test(b.brand || ""))) return true;
    if (navigator.userAgentData?.mobile === false && /TV/i.test(ua)) return true;
  } catch {
    // ignore
  }

  return false;
}

/**
 * Soft heuristic for TV-like living-room browsers that don't advertise as TV.
 * Requires a large landscape screen + no fine pointer hover (typical of remotes).
 * Does not alone turn on TV mode for tablets — only used as a hint with large screens.
 */
function detectTvEnvironment() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  const largeLandscape = window.matchMedia(
    "(min-width: 1280px) and (min-height: 720px) and (orientation: landscape)"
  ).matches;
  const remoteLike =
    window.matchMedia("(hover: none)").matches &&
    window.matchMedia("(pointer: coarse)").matches;
  // Very large screens (4K TV browsers often report ~1920–3840 CSS px)
  const livingRoom = window.matchMedia("(min-width: 1920px) and (min-height: 1000px)").matches;
  return (largeLandscape && remoteLike) || (livingRoom && remoteLike);
}

/**
 * Large living-room / web TV layout cues (safe on desktops too).
 * True at xl+ (1280px) landscape viewports.
 */
export function useIsLargeScreen() {
  return useMediaQuery("(min-width: 1280px) and (min-height: 700px)");
}

function detectTv() {
  return detectTvUserAgent() || detectTvEnvironment();
}

const TvModeContext = createContext({
  tvMode: false,
  autoDetected: false,
  setTvMode: () => {},
  toggleTvMode: () => {},
  isLargeScreen: false,
  cinemaActive: false,
  setCinemaActive: () => {},
});

export function TvModeProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isLargeScreen = useIsLargeScreen();
  const urlForced =
    searchParams.get("tv") === "1" ||
    searchParams.get("cinema") === "1" ||
    searchParams.get("tv") === "true";
  const urlOff = searchParams.get("tv") === "0";

  const [stored, setStored] = useState(() => readStoredPreference());
  const [autoDetected] = useState(() => detectTv());
  const [cinemaActive, setCinemaActive] = useState(false);

  const tvMode = useMemo(() => {
    if (urlOff) return false;
    if (urlForced) return true;
    if (stored === true) return true;
    if (stored === false) return false;
    return autoDetected;
  }, [urlForced, urlOff, stored, autoDetected]);

  const setTvMode = useCallback(
    (next) => {
      const enabled = !!next;
      setStored(enabled);
      try {
        localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
      } catch {
        // ignore
      }
      if (!enabled) setCinemaActive(false);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (enabled) {
            p.set("tv", "1");
            p.delete("cinema");
          } else {
            p.delete("tv");
            p.delete("cinema");
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const toggleTvMode = useCallback(() => {
    setTvMode(!tvMode);
  }, [setTvMode, tvMode]);

  useEffect(() => {
    const root = document.documentElement;
    if (tvMode) {
      root.classList.add("tv-mode");
      root.dataset.tvMode = "1";
    } else {
      root.classList.remove("tv-mode");
      delete root.dataset.tvMode;
    }
    return () => {
      root.classList.remove("tv-mode");
      delete root.dataset.tvMode;
    };
  }, [tvMode]);

  const value = useMemo(
    () => ({
      tvMode,
      autoDetected,
      setTvMode,
      toggleTvMode,
      isLargeScreen,
      cinemaActive: tvMode && cinemaActive,
      setCinemaActive,
    }),
    [tvMode, autoDetected, setTvMode, toggleTvMode, isLargeScreen, cinemaActive]
  );

  return <TvModeContext.Provider value={value}>{children}</TvModeContext.Provider>;
}

export function useTvMode() {
  return useContext(TvModeContext);
}
